// Named navigable places for 37 Kimbark Boulevard.
// Sub-room items use takeoff camera coords; rooms use KIMBARK_ROOMS coords.
// The `navigateToRoom` viewer action looks up entries here by placeName.

import type { KimbarkFloor } from './kimbarkRooms';
import { KIMBARK_KITCHEN_TAKEOFF } from './kimbarkKitchenTakeoff';
import { KIMBARK_FIREPLACE_TAKEOFF } from './kimbarkFireplaceTakeoff';
import { KIMBARK_STAIRCASE_TAKEOFF } from './kimbarkStaircaseTakeoff';

export interface KimbarkPlace {
  /** Canonical key Claude passes as `placeName`. */
  key: string;
  /** Aliases used to match user phrasing. */
  aliases: string[];
  /** Canonical room name — must match a KIMBARK_ROOMS entry for floor map highlight. */
  roomName: string;
  floor: KimbarkFloor;
  cameraPos: [number, number, number];
  cameraLookAt: [number, number, number];
  /** For sub-room items: sentence explaining which room they're in. */
  roomContext?: string;
}

export const KIMBARK_PLACES: KimbarkPlace[] = [
  // ── Sub-room items ────────────────────────────────────────────────────
  {
    key: 'living_room_divider',
    aliases: ['living room divider', 'divider', 'room divider', 'slat wall', 'slatted divider', 'wooden slats', 'slats', 'slatted wall', 'partition'],
    roomName: 'Living Room',
    floor: 'ground',
    cameraPos: [-1.495, 0.143, 5.136],
    cameraLookAt: [1.250, -0.897, -2.306],
    roomContext: 'The slatted divider separates the living room from the staircase on the Ground Floor.',
  },
  {
    key: 'fireplace',
    aliases: ['fireplace', 'fire place', 'mantle', 'mantel', 'hearth'],
    roomName: 'Living Room',
    floor: 'ground',
    cameraPos: KIMBARK_FIREPLACE_TAKEOFF.cameraPos,
    cameraLookAt: KIMBARK_FIREPLACE_TAKEOFF.cameraLookAt,
    roomContext: 'The fireplace is in the Living Room on the Ground Floor.',
  },
  {
    key: 'kitchen_island',
    aliases: ['kitchen island', 'island', 'marble island', 'counter', 'kitchen counter', 'countertop'],
    roomName: 'Kitchen',
    floor: 'ground',
    cameraPos: KIMBARK_KITCHEN_TAKEOFF.cameraPos,
    cameraLookAt: KIMBARK_KITCHEN_TAKEOFF.cameraLookAt,
    roomContext: 'The kitchen island is in the Kitchen on the Ground Floor.',
  },
  {
    key: 'staircase',
    aliases: ['staircase', 'stairs', 'stair', 'stairwell', 'stairway'],
    roomName: 'Hall',
    floor: 'ground',
    cameraPos: KIMBARK_STAIRCASE_TAKEOFF.cameraPos,
    cameraLookAt: KIMBARK_STAIRCASE_TAKEOFF.cameraLookAt,
    roomContext: 'The staircase is in the main Hall on the Ground Floor.',
  },

  // ── Ground floor rooms ────────────────────────────────────────────────
  {
    key: 'kitchen',
    aliases: ['kitchen', 'cook', 'cooking area'],
    roomName: 'Kitchen',
    floor: 'ground',
    cameraPos: [-1.452, 0.266, 3.583],
    cameraLookAt: [5.279, 0.085, 7.023],
  },
  {
    key: 'living_room',
    aliases: ['living room', 'living area', 'lounge', 'living'],
    roomName: 'Living Room',
    floor: 'ground',
    cameraPos: [-3.065, 0.120, 0.834],
    cameraLookAt: [-5.089, -0.834, 8.514],
  },
  {
    key: 'dining_area',
    aliases: ['dining', 'dining room', 'dining area', 'dinner'],
    roomName: 'Dining Area',
    floor: 'ground',
    cameraPos: [-2.542, 0.159, -1.066],
    cameraLookAt: [-8.111, -1.607, -6.531],
  },
  {
    key: 'breakfast_nook',
    aliases: ['breakfast nook', 'nook', 'breakfast area', 'breakfast'],
    roomName: 'Breakfast Nook',
    floor: 'ground',
    cameraPos: [-2.223, -0.655, 5.546],
    cameraLookAt: [3.334, -0.062, -0.178],
  },
  {
    key: 'foyer',
    aliases: ['foyer', 'entrance', 'entry', 'front door', 'front entry', 'lobby'],
    roomName: 'Foyer',
    floor: 'ground',
    cameraPos: [-0.050, 0.100, 3.183],
    cameraLookAt: [-0.050, 0.100, -4.817],
  },
  {
    key: 'ground_bathroom',
    aliases: ['bathroom', 'ground bathroom', 'main bathroom', 'powder room', 'washroom', 'half bath'],
    roomName: 'Bathroom',
    floor: 'ground',
    cameraPos: [0.533, -0.501, 5.333],
    cameraLookAt: [6.090, 0.092, -0.391],
  },
  {
    key: 'ground_hall',
    aliases: ['hallway', 'hall', 'corridor', 'ground floor hall'],
    roomName: 'Hall',
    floor: 'ground',
    cameraPos: [-4.530, 0.091, 3.935],
    cameraLookAt: [1.156, -0.211, -1.685],
  },

  // ── Basement rooms ────────────────────────────────────────────────────
  {
    key: 'sauna',
    aliases: ['sauna', 'steam room', 'steam'],
    roomName: 'Sauna',
    floor: 'basement',
    cameraPos: [-1.533, -2.491, 5.599],
    cameraLookAt: [4.583, -3.359, 10.683],
  },
  {
    key: 'laundry',
    aliases: ['laundry', 'laundry room', 'laundry area', 'washer', 'dryer'],
    roomName: 'Laundry',
    floor: 'basement',
    cameraPos: [0.184, -2.742, 3.639],
    cameraLookAt: [7.638, -2.990, 6.554],
  },

  // ── Upper floor rooms ─────────────────────────────────────────────────
  {
    key: 'primary_bedroom',
    aliases: ['primary bedroom', 'master bedroom', 'master', 'main bedroom', 'primary suite', 'master suite', 'upstairs bedroom', 'upstairs master', 'upper bedroom'],
    roomName: 'Primary Bedroom',
    floor: 'upper',
    cameraPos: [-0.679, 2.862, -3.866],
    cameraLookAt: [-8.373, 2.957, -6.058],
  },
  {
    key: 'sitting_room',
    aliases: ['sitting room', 'study', 'office', 'upper sitting', 'reading room', 'upstairs sitting room', 'upstairs study', 'upstairs office'],
    roomName: 'Sitting Room',
    floor: 'upper',
    cameraPos: [-1.819, 2.730, 3.210],
    cameraLookAt: [-7.782, 2.795, 8.542],
  },
  {
    key: 'wic',
    aliases: ['walk in closet', 'walk-in closet', 'wic', 'closet', 'wardrobe', 'dressing room', 'upstairs closet', 'upper closet'],
    roomName: 'W.I.C.',
    floor: 'upper',
    cameraPos: [-1.773, 2.718, -1.882],
    cameraLookAt: [-8.142, 2.029, 2.990],
  },
  {
    key: 'upper_bath',
    aliases: ['upper bathroom', 'upper bath', 'ensuite', 'en suite', 'upstairs bathroom', 'upstairs bath', 'upper washroom', 'master bathroom', 'master bath', 'primary bathroom', 'primary bath', 'bathroom upstairs'],
    roomName: 'Bath',
    floor: 'upper',
    cameraPos: [-2.868, 2.288, 0.186],
    cameraLookAt: [1.238, 2.694, 6.960],
    roomContext: 'The master bathroom (ensuite) is off the Primary Bedroom on the Upper Floor.',
  },
  {
    key: 'shower',
    aliases: ['shower', 'shower area', 'master shower', 'primary shower', 'shower in bathroom', 'shower in master', 'shower in primary bathroom', 'bathroom shower', 'repair work in shower', 'shower upstairs', 'upstairs shower', 'shower area upstairs', 'shower in primary bathroom upstairs'],
    roomName: 'Bath',
    floor: 'upper',
    cameraPos: [-3.164, 2.482, 0.119],
    cameraLookAt: [-1.505, 2.299, 7.943],
    roomContext: 'The shower stall is in the upper-floor master bathroom. Shower pipes run behind the tile wall.',
  },
];

export function findPlace(query: string): KimbarkPlace | undefined {
  const q = query.toLowerCase().trim();
  return KIMBARK_PLACES.find(p => p.aliases.some(a => q.includes(a)));
}

/** Compact description for the system prompt — lists key, parent room, and floor. */
export const KIMBARK_PLACES_PROMPT = KIMBARK_PLACES.map(p =>
  `  ${p.key}: "${p.aliases[0]}" → ${p.roomName} (${p.floor})${p.roomContext ? ' — ' + p.roomContext : ''}`
).join('\n');
